import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, MenuItem, Stack, TextField } from '@mui/material';
import { DataGrid } from '../../components/common/datagrid/ResponsiveDataGrid';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import * as approvalV2Api from '../../lib/api/approvalV2';

const DOC_TYPE_LABELS = {
  HR_LEAVE: '휴가 신청',
  ORDER_BOOK: '수주품의서',
};

const DOC_STATUS_LABELS = {
  IN_PROGRESS: '진행중',
  APPROVED: '승인',
  REJECTED: '반려',
  RECALLED: '회수',
  CANCELED: '취소',
};

const DOC_STATUS_COLORS = {
  IN_PROGRESS: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  RECALLED: 'default',
  CANCELED: 'default',
};

const ApprovalInboxV2Container = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docTypeFilter, setDocTypeFilter] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await approvalV2Api.getApprovalV2Inbox();
        setList(res.data.list || []);
      } catch (e) {
        enqueueSnackbar(e?.response?.data?.message || '결재함 조회에 실패했습니다.', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [enqueueSnackbar]);

  const filteredList = useMemo(() => {
    return (list || []).filter((row) => {
      const matchesDocType = !docTypeFilter || row.docType === docTypeFilter;
      const matchesKeyword = !keyword.trim()
        || [row.refDocNo, row.docTitle, row.submittedByNm, row.currentActorNm, row.originalAssigneeNm]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(keyword.trim().toLowerCase()));
      return matchesDocType && matchesKeyword;
    });
  }, [docTypeFilter, keyword, list]);

  const columns = [
    {
      field: 'docType',
      headerName: '문서유형',
      minWidth: 120,
      flex: 0.6,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={DOC_TYPE_LABELS[value] || value}
          variant="outlined"
        />
      ),
    },
    { field: 'refDocNo', headerName: '문서번호', minWidth: 170, flex: 0.8 },
    { field: 'docTitle', headerName: '문서명', minWidth: 280, flex: 1.4 },
    { field: 'submittedByNm', headerName: '기안자', minWidth: 100, flex: 0.5 },
    {
      field: 'myStepOrder',
      headerName: '내 단계',
      minWidth: 90,
      flex: 0.4,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'currentActorNm',
      headerName: '현재 결재자',
      minWidth: 120,
      flex: 0.6,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'originalAssigneeNm',
      headerName: '원결재자',
      minWidth: 120,
      flex: 0.6,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'docStatus',
      headerName: '상태',
      minWidth: 110,
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={DOC_STATUS_LABELS[value] || value}
          color={DOC_STATUS_COLORS[value] || 'default'}
        />
      ),
    },
    {
      field: 'submittedAt',
      headerName: '상신일',
      minWidth: 160,
      flex: 0.8,
      valueFormatter: (value) => value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
  ];

  return (
    <>
      <BreadcrumbsContainer title="결재관리/결재함" />
      <Alert severity="info" sx={{ mb: 2 }}>
        approval v2 문서를 기준으로 결재함을 조회합니다. 행을 클릭하면 상세에서 승인, 반려, 회수를 처리할 수 있습니다.
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="문서유형"
          value={docTypeFilter}
          onChange={(event) => setDocTypeFilter(event.target.value)}
          sx={{ width: 160 }}
        >
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="HR_LEAVE">휴가 신청</MenuItem>
          <MenuItem value="ORDER_BOOK">수주품의서</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="검색어"
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          sx={{ width: 320 }}
        />
        <Box sx={{ flex: 1 }} />
      </Stack>

      <DataGrid
        rows={filteredList}
        columns={columns}
        getRowId={(row) => row.apprDocId}
        loading={loading}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(`/approvalmng/${params.row.apprDocId}`, {
          state: { from: 'inbox' },
        })}
        sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
      />
    </>
  );
};

export default ApprovalInboxV2Container;
